import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import { api } from "./lib/api";

import StoreLayout from "./layouts/StoreLayout";
import AdminLayout from "./layouts/AdminLayout";

import Home from "./pages/store/Home";
import Catalog from "./pages/store/Catalog";
import ProductDetail from "./pages/store/ProductDetail";
import Checkout from "./pages/store/Checkout";
import OrderSuccess from "./pages/store/OrderSuccess";
import Account from "./pages/store/Account";
import StaticPage from "./pages/store/StaticPage";
import SharedWishlist from "./pages/store/SharedWishlist";

import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import AdminOrders from "./pages/admin/Orders";
import AdminLedger from "./pages/admin/Ledger";
import AdminInventory from "./pages/admin/Inventory";
import AdminCustomers from "./pages/admin/Customers";
import AdminAssistant from "./pages/admin/Assistant";
import AdminSmsLogs from "./pages/admin/SmsLogs";
import AdminPromotions from "./pages/admin/Promotions";
import AdminCategories from "./pages/admin/Categories";
import AdminStorefrontSettings from "./pages/admin/StorefrontSettings";
import AdminCompanySettings from "./pages/admin/CompanySettings";
import AdminWholesalers from "./pages/admin/Wholesalers";
import AdminPurchases from "./pages/admin/Purchases";
import Wholesale from "./pages/wholesale/Wholesale";
import WholesaleLogin from "./pages/wholesale/WholesaleLogin";
import WholesaleEnquiry from "./pages/wholesale/WholesaleEnquiry";
import AdminLogin from "./pages/admin/Login";
import Login from "./pages/store/Login";
import Register from "./pages/store/Register";
import PhoneLogin from "./pages/store/PhoneLogin";
import ForgotPassword from "./pages/store/ForgotPassword";
import ResetPassword from "./pages/store/ResetPassword";
import PayLink from "./pages/store/PayLink";

function ScrollToTop() {
  // On every pathname change, snap the window to the top so the newly
  // mounted page starts at its hero/products area instead of the previous
  // scroll offset (which was often the footer).
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AuthCallback() {
  const location = useLocation();
  const ran = useRef(false);
  const { setUser } = useAuth();
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = new URLSearchParams(location.hash.replace(/^#/, ""));
    const session_id = hash.get("session_id");
    if (!session_id) { window.location.href = "/"; return; }
    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id });
        setUser(r.data);
        window.location.href = r.data.is_admin ? "/admin" : "/account";
      } catch (e) {
        window.location.href = "/";
      }
    })();
  }, [location, setUser]);
  return (
    <div className="min-h-screen flex items-center justify-center text-stone-600 font-serif-display text-2xl">
      Signing you in…
    </div>
  );
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route element={<StoreLayout />}>
        <Route index element={<Home />} />
        <Route path="/shop" element={<Catalog />} />
        <Route path="/shop/:category" element={<Catalog />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order/:orderId" element={<OrderSuccess />} />
        <Route path="/account" element={<Account />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/phone" element={<PhoneLogin />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/wishlist/:token" element={<SharedWishlist />} />
        <Route path="/about" element={<StaticPage page="about" />} />
        <Route path="/contact" element={<StaticPage page="contact" />} />
        <Route path="/faq" element={<StaticPage page="faq" />} />
        <Route path="/shipping" element={<StaticPage page="shipping" />} />
        <Route path="/returns" element={<StaticPage page="returns" />} />
        <Route path="/privacy" element={<StaticPage page="privacy" />} />
        <Route path="/terms" element={<StaticPage page="terms" />} />
      </Route>

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/pay/:orderId" element={<PayLink />} />
      <Route path="/wholesale/login" element={<WholesaleLogin />} />
      <Route path="/wholesale/enquiry" element={<WholesaleEnquiry />} />
      <Route path="/wholesale" element={<Wholesale />} />
      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="ledger" element={<AdminLedger />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="sms-logs" element={<AdminSmsLogs />} />
        <Route path="promotions" element={<AdminPromotions />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="storefront" element={<AdminStorefrontSettings />} />
        <Route path="company" element={<AdminCompanySettings />} />
        <Route path="wholesalers" element={<AdminWholesalers />} />
        <Route path="purchases" element={<AdminPurchases />} />
        <Route path="assistant" element={<AdminAssistant />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <AppRouter />
              <Toaster richColors position="top-right" />
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
